import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FormDialog } from "@/components/FormDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ROOT = "__root__";
const fields = (locations, self) => [
  { name: "name", label: "Nombre", required: true, placeholder: "Ej. Anaquel 1" },
  { name: "parent_id", label: "Dentro de", type: "select", options: [{ value: ROOT, label: "— Nivel principal —" },
    ...locations.filter((l) => l.id !== self && !l.path.startsWith(`${locations.find((x) => x.id === self)?.path} /`)).map((l) => ({ value: l.id, label: l.path }))] },
  { name: "order", label: "Orden", type: "number", default: 100 },
];

const depth = (l) => l.path.split(" / ").length - 1;

export const LocationsTab = ({ locations, reload }) => {
  const { can } = useAuth();
  const [dlg, setDlg] = useState(null);
  const editable = can("inventory.write");
  const save = async (f) => {
    const body = { ...f, parent_id: f.parent_id === ROOT ? null : f.parent_id, order: Number(f.order) || 100 };
    try {
      if (dlg.loc) await api.patch(`/inventory/locations/${dlg.loc.id}`, body); else await api.post("/inventory/locations", body);
      toast.success("Ubicación guardada"); setDlg(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (l, active) => {
    try { await api.patch(`/inventory/locations/${l.id}`, { active }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="inventory-locations-tab">
      {editable && <Button onClick={() => setDlg({})} data-testid="loc-add-button"><Plus className="mr-2 h-4 w-4" />Nueva ubicación</Button>}
      <div className="rounded-lg border border-border">
        {locations.map((l) => (
          <div key={l.id} className="flex items-center gap-3 border-b border-border/60 px-3 py-2 last:border-0" style={{ paddingLeft: 12 + depth(l) * 24 }} data-testid={`loc-row-${l.id}`}>
            <span className={`text-sm ${l.active ? "" : "text-muted-foreground line-through"}`}>{l.name}</span>
            <span className="text-xs text-muted-foreground">{depth(l) ? l.path : "Almacén / nivel principal"}</span>
            {editable && <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDlg({ parent: l.id })} data-testid={`loc-add-child-${l.id}`}><Plus className="mr-1 h-3.5 w-3.5" />Sub-ubicación</Button>
              <Button size="icon" variant="ghost" onClick={() => setDlg({ loc: l })} data-testid={`loc-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
              <Switch checked={l.active} onCheckedChange={(v) => toggle(l, v)} data-testid={`loc-active-${l.id}`} />
            </div>}
          </div>))}
        {!locations.length && <p className="p-4 text-sm text-muted-foreground">Sin ubicaciones. Crea, por ejemplo, "Almacén Principal".</p>}
      </div>
      <FormDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.loc ? "Editar ubicación" : "Nueva ubicación"} fields={fields(locations, dlg?.loc?.id)}
        initial={dlg?.loc ? { name: dlg.loc.name, parent_id: dlg.loc.parent_id || ROOT, order: dlg.loc.order } : { parent_id: dlg?.parent || ROOT }} onSubmit={save} testid="loc-form" />
    </div>
  );
};
